import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { getSharedOverlays, type OverlayKey, type OverlayState } from '../overlays/OverlayService';
import { GNSS_COLORS } from '../globe/gnssColoring';

interface OverlayMeta {
  key: OverlayKey;
  glyph: string;
  label: string;
  description: string;
}

const OVERLAYS: ReadonlyArray<OverlayMeta> = [
  {
    key: 'ribbons',
    glyph: '〜',
    label: 'Orbit ribbon',
    description: '±N orbits ground track for the selected satellite',
  },
  {
    key: 'marquee',
    glyph: '◰',
    label: '3D shapes',
    description: 'Boxes/cylinders for ISS, Tiangong, Hubble & cargo capsules when zoomed in',
  },
  {
    key: 'stations',
    glyph: '⏚',
    label: 'Ground stations',
    description: '~40 NEN / DSN / ESTRACK / JAXA / ISRO / commercial sites with 5° FOV cones',
  },
  {
    key: 'lightPollution',
    glyph: '☀',
    label: 'Light pollution',
    description: 'NASA VIIRS 2012 city-lights raster — visible on the night side only, lazy-loaded',
  },
  {
    key: 'aurora',
    glyph: '✦',
    label: 'Aurora forecast',
    description: 'NOAA OVATION 30-min aurora probability raster — refreshed every 15min server-side',
  },
  {
    key: 'sunSynchRibbons',
    glyph: '⤳',
    label: 'Sun-synchronous ribbons',
    description: 'Ground tracks for Landsat 8/9 + Sentinel-1/2/3/5P — dawn/dusk terminator-following polar orbits',
  },
  {
    key: 'decayTrails',
    glyph: '⌫',
    label: 'Decay trails',
    description: 'Last 30 days of predicted reentries as fading red ground tracks — needs Space-Track TIP coverage',
  },
  {
    key: 'amateurStations',
    glyph: '📡',
    label: 'Amateur ground stations',
    description: '~2,000 active SatNOGS community SDR receivers worldwide — click for SatNOGS Network deep-link',
  },
  {
    key: 'constellations',
    glyph: '✦',
    label: 'Constellations',
    description: 'Stick-figure lines + labels for major constellations (Orion, the Dippers, the zodiac, Crux, Centaurus, …) anchored to the BSC5 starfield',
  },
  {
    key: 'passArcs',
    glyph: '↗',
    label: 'Pass arcs (sky view)',
    description: 'Rise → peak → set polylines for the marquee roster (ISS, Tiangong, Hubble, JWST, current cargo + crew capsules) plus RCS-estimated naked-eye-bright candidates over the next 24 hours. Sky view only.',
  },
];

/**
 * <sat-overlays-menu>
 *
 * Topbar dropdown that toggles the Phase 3 visual overlays.
 * Subscribes to OverlayService so other components can mutate state
 * (or storage rehydrate) without us having to poll.
 */
@customElement('sat-overlays-menu')
export class SatOverlaysMenu extends LitElement {
  @state() private open = false;
  @state() private overlayState: OverlayState = {
    ribbons: true,
    marquee: true,
    stations: false,
    lightPollution: false,
    aurora: false,
    gnssGps: false,
    gnssGalileo: false,
    gnssGlonass: false,
    gnssBeidou: false,
    sunSynchRibbons: false,
    decayTrails: false,
    amateurStations: false,
    constellations: false,
    passArcs: true,
  };

  private unsubscribe: (() => void) | null = null;

  static styles = css`
    :host {
      position: relative;
      display: inline-block;
      font-family: var(--font-mono);
    }
    .toggle {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      padding: 0.3rem 0.6rem;
      background: transparent;
      color: var(--color-text);
      border: 1px solid var(--color-border);
      border-radius: 4px;
      font-family: var(--font-mono);
      font-size: 0.85rem;
      cursor: pointer;
    }
    .toggle:hover { border-color: var(--color-accent); color: var(--color-accent); }
    .toggle__glyph { font-size: 0.95rem; line-height: 1; }

    /* Collapse the "Overlays" label below 1300px — the ⊞ glyph plus
       the active-count badge still communicate state. */
    @media (max-width: 1300px) {
      .toggle__label { display: none; }
    }
    .menu {
      position: absolute;
      top: calc(100% + 0.4rem);
      right: 0;
      min-width: 320px;
      background: var(--color-bg-elevated);
      border: 1px solid var(--color-border);
      border-radius: 4px;
      padding: 0.4rem 0;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
      z-index: 100;
    }
    .menu h3 {
      margin: 0;
      padding: 0 0.8rem 0.4rem;
      font-size: 0.7rem;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--color-text-dim);
    }
    .item {
      display: flex;
      align-items: flex-start;
      gap: 0.6rem;
      padding: 0.45rem 0.8rem;
      cursor: pointer;
      transition: background 0.1s ease;
    }
    .item:hover { background: rgba(255, 255, 255, 0.04); }
    .item__check {
      flex-shrink: 0;
      width: 1rem;
      height: 1rem;
      margin-top: 0.15rem;
      border: 1px solid var(--color-border);
      border-radius: 2px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.75rem;
      color: var(--color-bg);
      background: transparent;
    }
    .item__check[data-on='true'] {
      background: var(--color-accent);
      border-color: var(--color-accent);
    }
    .item__glyph {
      flex-shrink: 0;
      font-size: 0.95rem;
      width: 1rem;
      text-align: center;
      color: var(--color-text-muted);
    }
    .item__body { display: flex; flex-direction: column; gap: 0.1rem; }
    .item__label { font-size: 0.85rem; color: var(--color-text); }
    .item__desc { font-size: 0.7rem; color: var(--color-text-dim); }

    /* Phase 7 chunk 2A — visually mark overlays that sky view has
       suspended (the click handler also short-circuits server-side via
       OverlayService.setEnabled refusing writes to suspended keys, but
       the CSS makes the lock state legible to the user). */
    .item[data-suspended='true'] {
      opacity: 0.45;
      cursor: not-allowed;
    }
    .item[data-suspended='true']:hover {
      background: transparent;
    }
    .item__lock {
      flex-shrink: 0;
      font-size: 0.7rem;
      color: var(--color-text-dim);
      align-self: flex-start;
      margin-top: 0.2rem;
    }

    /* Phase 9 chunk 2 — GNSS color swatch in the constellation rows.
       Squared with the same width as .item__check so the columns line
       up; the inline background-color is set per-row via the style
       attribute (one per constellation). */
    .item__swatch {
      flex-shrink: 0;
      width: 1rem;
      height: 1rem;
      margin-top: 0.15rem;
      border-radius: 50%;
      border: 1px solid var(--color-border);
    }

    /* GNSS section header sits between two horizontal rules so the
       grouping is visible without a heavy box. */
    .gnss-section {
      border-top: 1px solid var(--color-border);
      margin-top: 0.3rem;
      padding-top: 0.4rem;
    }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    this.unsubscribe = getSharedOverlays().subscribe((s) => { this.overlayState = s; });
    document.addEventListener('click', this.handleOutsideClick);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.unsubscribe = null;
    document.removeEventListener('click', this.handleOutsideClick);
  }

  private handleOutsideClick = (e: MouseEvent): void => {
    if (!e.composedPath().includes(this)) {
      this.open = false;
    }
  };

  private toggle(): void {
    this.open = !this.open;
  }

  private onItemClick(key: OverlayKey): void {
    getSharedOverlays().setEnabled(key, !this.overlayState[key]);
  }

  /**
   * Phase 9 chunk 2 — `§ GNSS` subsection. Renders four toggleable
   * rows, one per constellation, each with a color swatch acting as
   * both control affordance and inline legend (the swatch's color
   * matches what the corresponding dots paint on the globe when the
   * row is checked).
   */
  private renderGnssSection() {
    const constellations: ReadonlyArray<{ key: OverlayKey; label: string; color: string; description: string }> = [
      { key: 'gnssGps',     label: 'GPS',     color: GNSS_COLORS.gps,     description: 'Operational GPS satellites (~30) — amber' },
      { key: 'gnssGalileo', label: 'Galileo', color: GNSS_COLORS.galileo, description: 'European Galileo constellation (~30) — blue' },
      { key: 'gnssGlonass', label: 'GLONASS', color: GNSS_COLORS.glonass, description: 'Russian GLONASS constellation (~25) — green' },
      { key: 'gnssBeidou',  label: 'BeiDou',  color: GNSS_COLORS.beidou,  description: 'Chinese BeiDou constellation (~50) — red-amber' },
    ];
    return html`
      <h3 class="gnss-section">§ GNSS constellations</h3>
      ${constellations.map((c) => html`
        <div
          class="item"
          role="menuitemcheckbox"
          aria-checked=${this.overlayState[c.key] ? 'true' : 'false'}
          @click=${() => this.onItemClick(c.key)}
        >
          <span class="item__check" data-on=${this.overlayState[c.key] ? 'true' : 'false'}>${this.overlayState[c.key] ? '✓' : ''}</span>
          <span class="item__swatch" aria-hidden="true" style=${`background: ${c.color};`}></span>
          <span class="item__body">
            <span class="item__label">${c.label}</span>
            <span class="item__desc">${c.description}</span>
          </span>
        </div>
      `)}
    `;
  }

  render() {
    const enabledCount = Object.values(this.overlayState).filter(Boolean).length;
    return html`
      <button class="toggle" @click=${this.toggle} aria-haspopup="menu" aria-expanded=${this.open ? 'true' : 'false'}>
        <span class="toggle__glyph" aria-hidden="true">⊞</span>
        <span class="toggle__label">Overlays</span>
        <span style="color: var(--color-text-dim); font-size: 0.75rem;">${enabledCount}</span>
      </button>
      ${this.open
        ? html`
            <div class="menu" role="menu">
              <h3>Visual overlays</h3>
              ${OVERLAYS.map((o) => {
                const suspended = getSharedOverlays().isSuspended(o.key);
                return html`
                <div
                  class="item"
                  role="menuitemcheckbox"
                  aria-checked=${this.overlayState[o.key] ? 'true' : 'false'}
                  aria-disabled=${suspended ? 'true' : 'false'}
                  data-suspended=${suspended ? 'true' : 'false'}
                  title=${suspended ? 'Hidden in sky view' : ''}
                  @click=${() => this.onItemClick(o.key)}
                >
                  <span class="item__check" data-on=${this.overlayState[o.key] ? 'true' : 'false'}>${this.overlayState[o.key] ? '✓' : ''}</span>
                  <span class="item__glyph" aria-hidden="true">${o.glyph}</span>
                  <span class="item__body">
                    <span class="item__label">${o.label}</span>
                    <span class="item__desc">${o.description}</span>
                  </span>
                  ${suspended ? html`<span class="item__lock" aria-hidden="true">🔒</span>` : nothing}
                </div>
              `;
              })}
              ${this.renderGnssSection()}
            </div>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sat-overlays-menu': SatOverlaysMenu;
  }
}
